import { create } from 'zustand';
import type { AppointmentFormValues } from '@/types/appointment-form';

/**
 * Cross-domain handoff for "Create Appointment" launched OUTSIDE the
 * scheduling module (e.g. the patient-list row kebab). Lives in `shared/`
 * because `patients/` may not import `scheduling/` and vice-versa
 * (CLAUDE.md structural rule) — both, plus the app shell, import this.
 *
 * Flow (product decision 2026-06-02 — overlay, NOT a nav switch):
 *   1. Patient-list kebab → `requestCreateAppointment(prefill)`.
 *   2. `App.tsx` watches `pendingPrefill`; when set, it renders the calendar's
 *      `CreateAppointmentPanel` as an OVERLAY over the current surface — the
 *      user is NOT taken to the Calendar. Booking shows its own toast and
 *      calls onClose → `clear()`, dismissing the overlay in place.
 *
 * The payload is a generic `Partial<AppointmentFormValues>` — the
 * patient→form mapping is built in `patients/` (which owns PatientDatum);
 * scheduling only receives form values, so neither domain imports the other.
 */
/**
 * Narrows the create-appointment provider list to one discipline. Plain data (no function), so it can
 * sit in the store. `preferredProviderId` is picked for the user when that provider is in the narrowed list.
 */
export interface ProviderFilter {
  discipline: 'dietetics';
  /** Singular role name for messages, e.g. "Dietitian". */
  label: string;
  preferredProviderId?: string | null;
}

/**
 * Optional second argument of `requestCreateAppointment`. `onBooked` receives the id of the
 * appointment the panel saved (PROD-1395: the Referrals desk marks its referral booked). Callers
 * that pass nothing behave exactly as before.
 */
export interface CreateAppointmentRequestOptions {
  /** Narrow the panel's provider list to one discipline (referral handoffs only). */
  providerFilter?: ProviderFilter;
  onBooked?: (appointmentId: string) => void;
  /** The save succeeded but returned no appointment id, so `onBooked` cannot run (referral handoffs only). */
  onBookedWithoutId?: () => void;
}

interface CreateAppointmentHandoffState {
  pendingPrefill: Partial<AppointmentFormValues> | null;
  pendingProviderFilter: ProviderFilter | null;
  pendingOnBooked: ((appointmentId: string) => void) | null;
  pendingOnBookedWithoutId: (() => void) | null;
  requestCreateAppointment: (
    prefill: Partial<AppointmentFormValues>,
    options?: CreateAppointmentRequestOptions,
  ) => void;
  /** One-shot preselect: forget the preferred provider, keep the discipline filter. */
  consumePreferredProvider: () => void;
  clear: () => void;
}

export const useCreateAppointmentHandoff =
  create<CreateAppointmentHandoffState>((set) => ({
    pendingPrefill: null,
    pendingProviderFilter: null,
    pendingOnBooked: null,
    pendingOnBookedWithoutId: null,
    requestCreateAppointment: (prefill, options) =>
      set({
        pendingPrefill: prefill,
        pendingProviderFilter: options?.providerFilter ?? null,
        pendingOnBooked: options?.onBooked ?? null,
        pendingOnBookedWithoutId: options?.onBookedWithoutId ?? null,
      }),
    consumePreferredProvider: () =>
      set((s) => (s.pendingProviderFilter ? { pendingProviderFilter: { ...s.pendingProviderFilter, preferredProviderId: null } } : s)),
    clear: () => set({ pendingPrefill: null, pendingProviderFilter: null, pendingOnBooked: null, pendingOnBookedWithoutId: null }),
  }));

/**
 * Turns a pending `onBooked` into the panel's `onSaveSuccess`. No `onBooked` gives `undefined`, so a
 * handoff that never asked for the id leaves the panel exactly as it was. A save that returns no
 * appointment id calls nothing.
 */
export function bookedToSaveSuccess(
  onBooked: ((appointmentId: string) => void) | null,
  onBookedWithoutId?: (() => void) | null,
): ((saved?: { appointmentId?: string }) => void) | undefined {
  if (!onBooked) return undefined;
  return (saved) => {
    if (saved?.appointmentId) onBooked(saved.appointmentId);
    else onBookedWithoutId?.();
  };
}
